/**
 * Projects (plan.md Phase 5): one GitHub repo, its live address, its business,
 * its rooms. The page shows the card agents get and which repo docs they read;
 * admins create projects, admins and the project's people edit them.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { DecisionList } from "../components/Decisions";
import { byUrgency, CiDot, TaskRow, useTaskList } from "../components/Tasks";
import { Avatar, ErrorNote, Fold, Modal } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { loadBootstrap, useStore, type CiState, type Person, type Project } from "../lib/store";

const MODE = {
  manual: { label: "Manual delivery", help: "Agents stop at the pull request with its preview and review. You merge and deploy yourself." },
  automatic: { label: "Automatic delivery", help: "After you confirm, the coordinator merges and deploys with the project's own deploy." },
} as const;

interface Detail {
  project: Project;
  canEdit: boolean;
  docs: { sha: string; files: { path: string; chars: number; cut: boolean }[] } | null;
  docsError: string | null;
}

export function ProjectPage() {
  const { projectId } = useParams();
  const people = useStore((s) => s.boot!.people);
  const projects = useStore((s) => s.boot!.projects);
  const isAdmin = useStore((s) => s.boot?.workspace.role === "admin");
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const reload = () => api<Detail>(`/api/projects/${projectId}`).then(setDetail, (err) => setError(err instanceof ApiError ? err.message : "Could not load the project."));
  // The bootstrap list changes when someone edits a project or links a room.
  const listed = projects.find((p) => p.id === projectId);
  useEffect(() => {
    setDetail(null);
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, JSON.stringify(listed)]);

  if (error) return <main className="page"><ErrorNote error={error} /></main>;
  if (!detail) return <main className="page"><p className="muted-text">Loading</p></main>;
  const p = detail.project;
  const team = p.peopleIds.map((id) => people.find((x) => x.id === id)).filter(Boolean) as Person[];
  return (
    <main className="page project-page">
      <header className="block-head">
        <div>
          <h1>{p.name}</h1>
          <p className="muted-text">
            <a href={`https://github.com/${p.repo}`} target="_blank" rel="noreferrer">
              {p.repo}
            </a>{" "}
            · branch {p.defaultBranch}
            {p.liveUrl && (
              <>
                {" "}
                ·{" "}
                <a href={p.liveUrl} target="_blank" rel="noreferrer">
                  {p.liveUrl.replace(/^https?:\/\//, "")}
                </a>
              </>
            )}
          </p>
        </div>
        <div className="actions">
          <span className="chip">{MODE[p.deliveryMode].label}</span>
          {p.status !== "active" && <span className="chip">{p.status}</span>}
          {detail.canEdit && (
            <button className="btn ghost small" type="button" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
        </div>
      </header>

      <ProjectOverview projectId={p.id} repo={p.repo} liveUrl={p.liveUrl} />

      <Fold id="project-about" title="About" summary={`${p.business || "No business set"} · ${MODE[p.deliveryMode].label} · workers ${p.workersEnabled ? "on" : "off"}`}>
        <dl className="profile-facts">
          <dt>Business</dt>
          <dd>{p.business || "not set"}</dd>
          <dt>Delivery</dt>
          <dd>{MODE[p.deliveryMode].help}</dd>
          <dt>Coding workers</dt>
          <dd>{p.workersEnabled ? `On (${p.workerWorkflow})` : "Off"}</dd>
          <dt>Previews</dt>
          <dd>{p.previewPattern || "not set"}</dd>
          <dt>Folder on the Mac</dt>
          <dd>{p.folderPath ? <code>{p.folderPath}</code> : "not set"}</dd>
          <dt>People</dt>
          <dd>
            {team.length ? (
              <span className="people-inline">
                {team.map((x) => (
                  <span key={x.id}>
                    <Avatar person={x} size={20} /> {x.name}
                  </span>
                ))}
              </span>
            ) : (
              "nobody yet"
            )}
          </dd>
        </dl>
      </Fold>

      <Fold id="project-rooms" title="Rooms" summary={p.rooms.length ? p.rooms.map((r) => `#${r.name}`).join(", ") : "No room linked yet."}>
        <p className="muted-text">Agents in these rooms get this project's card and docs with every question. Link a room from its panel.</p>
        {p.rooms.length ? (
          <ul className="rows">
            {p.rooms.map((r) => (
              <li key={r.id}>
                <div>
                  <Link to={`/r/${r.id}`}>#{r.name}</Link>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted-text">No room linked yet.</p>
        )}
      </Fold>

      <ProjectTasks projectId={p.id} />

      <ClaudePolicy projectId={p.id} canEdit={isAdmin} />

      <Fold id="project-decisions" title="Decisions">
        <p className="muted-text">Saved from messages in this project's rooms. Every agent in these rooms reads them with each question.</p>
        <DecisionList url={`/api/projects/${p.id}/decisions`} empty="No decisions yet. In a room, open a message's More menu and pick Save as decision." />
      </Fold>

      <Fold id="project-docs" title="What agents read" summary={detail.docs ? `${detail.docs.files.length} docs from GitHub: ${detail.docs.files.map((f) => f.path).join(", ")}` : undefined}>
        {detail.docs ? (
          detail.docs.files.length ? (
            <>
              <p className="muted-text">
                From GitHub at commit <code>{detail.docs.sha.slice(0, 7)}</code>, refreshed when the branch moves. Agora keeps no copy. Agents can also open any other file of the repo.
              </p>
              <ul className="rows">
                {detail.docs.files.map((f) => (
                  <li key={f.path}>
                    <div>
                      <a href={`https://github.com/${p.repo}/blob/${p.defaultBranch}/${f.path}`} target="_blank" rel="noreferrer">
                        {f.path}
                      </a>
                      <small>
                        {Math.round(f.chars / 100) / 10}k characters{f.cut ? ", the first 16k go with each question" : ""}
                      </small>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="muted-text">The repo has no CLAUDE.md, AGENTS.md, plan.md or README.md yet.</p>
          )
        ) : (
          <p className="muted-text">GitHub could not be read: {detail.docsError}</p>
        )}
      </Fold>

      {editing && <ProjectForm project={p} onClose={() => setEditing(false)} onSaved={() => (setEditing(false), void loadBootstrap(), void reload())} />}
    </main>
  );
}

interface Repo {
  fullName: string;
  defaultBranch: string;
  private: boolean;
  description: string;
}

/** New project (admins) or edit (admins and the project's people). */
export function ProjectForm({ project, onClose, onSaved }: { project?: Project; onClose: () => void; onSaved: (id: string) => void }) {
  const people = useStore((s) => s.boot!.people);
  const me = useStore((s) => s.boot!.me.id);
  const projects = useStore((s) => s.boot!.projects);
  const taken = useMemo(() => projects.map((p) => p.repo.toLowerCase()), [projects]);
  const [repos, setRepos] = useState<Repo[] | null>(null);
  const [reposError, setReposError] = useState<string | null>(null);
  const [repo, setRepo] = useState(project?.repo ?? "");
  const [name, setName] = useState(project?.name ?? "");
  const [liveUrl, setLiveUrl] = useState(project?.liveUrl ?? "");
  const [previewPattern, setPreviewPattern] = useState(project?.previewPattern ?? "");
  const [business, setBusiness] = useState(project?.business ?? "");
  const [folderPath, setFolderPath] = useState(project?.folderPath ?? "");
  const [deliveryMode, setDeliveryMode] = useState<Project["deliveryMode"]>(project?.deliveryMode ?? "manual");
  const [status, setStatus] = useState<Project["status"]>(project?.status ?? "active");
  const [workers, setWorkers] = useState(project?.workersEnabled ?? false);
  const [workflow, setWorkflow] = useState(project?.workerWorkflow ?? "agora-worker.yml");
  const admin = useStore((s) => s.boot!.workspace.role === "admin");
  const [team, setTeam] = useState<string[]>(project?.peopleIds ?? [me]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (project) return;
    api<{ repos: Repo[] }>("/api/github/repos").then(
      (r) => setRepos(r.repos.filter((x) => !taken.includes(x.fullName.toLowerCase()))),
      (err) => setReposError(err instanceof ApiError ? err.message : "Could not load the repos."),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = { name, liveUrl, previewPattern, business, folderPath, deliveryMode, peopleIds: team };
    try {
      if (project) {
        await api(`/api/projects/${project.id}`, { ...body, status, ...(admin ? { workersEnabled: workers, workerWorkflow: workflow } : {}) }, "PATCH");
        onSaved(project.id);
      } else {
        const r = await api<{ id: string }>("/api/projects", { ...body, repo });
        onSaved(r.id);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save.");
      setBusy(false);
    }
  }

  return (
    <Modal title={project ? `Edit ${project.name}` : "New project"} onClose={onClose} wide>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        {!project && (
          <label className="field">
            <span>GitHub repo</span>
            {repos ? (
              <select
                value={repo}
                required
                onChange={(e) => {
                  setRepo(e.target.value);
                  if (!name) setName(e.target.value.split("/")[1] ?? "");
                }}
              >
                <option value="" disabled>
                  {repos.length ? `Choose one of the ${repos.length} repos Agora can see` : "Every repo Agora can see has a project already"}
                </option>
                {repos.map((r) => (
                  <option key={r.fullName} value={r.fullName}>
                    {r.fullName}
                    {r.description ? `  ·  ${r.description.slice(0, 60)}` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <input value={repo} disabled placeholder={reposError ? "" : "Loading the repos"} />
            )}
            <small>
              {reposError ? (
                <>
                  {reposError}. <Link to="/settings#github">Connect GitHub in Settings</Link>.
                </>
              ) : (
                "Missing a repo? Add it to Agora's GitHub App in Settings, GitHub, Change repos."
              )}
            </small>
          </label>
        )}
        <label className="field">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required placeholder="For example LoanProGuru" />
        </label>
        <label className="field">
          <span>Live address</span>
          <input type="url" value={liveUrl} onChange={(e) => setLiveUrl(e.target.value)} placeholder="https://loanproguru.com" />
        </label>
        <label className="field">
          <span>Business or brand</span>
          <input value={business} onChange={(e) => setBusiness(e.target.value)} maxLength={300} placeholder="Who it is for, what it sells" />
        </label>
        <label className="field">
          <span>Delivery</span>
          <select value={deliveryMode} onChange={(e) => setDeliveryMode(e.target.value as Project["deliveryMode"])}>
            <option value="manual">{MODE.manual.label}</option>
            <option value="automatic">{MODE.automatic.label}</option>
          </select>
          <small>{MODE[deliveryMode].help}</small>
        </label>
        <label className="field">
          <span>Preview address (optional)</span>
          <input value={previewPattern} onChange={(e) => setPreviewPattern(e.target.value)} maxLength={300} placeholder="https://test.loanproguru.com" />
        </label>
        <label className="field">
          <span>Folder on the Mac (optional)</span>
          <input value={folderPath} onChange={(e) => setFolderPath(e.target.value)} maxLength={300} placeholder="/Users/sergiolonghi/Antygravity/LoanProGuru" />
        </label>
        {project && (
          <label className="field">
            <span>Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value as Project["status"])}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived (agents stop getting it)</option>
            </select>
          </label>
        )}
        {project && admin && (
          <label className="field">
            <span>Coding workers</span>
            <span className="check">
              <input type="checkbox" checked={workers} onChange={(e) => setWorkers(e.target.checked)} />
              On: Approve on a task starts a Claude Code worker on GitHub (needs the worker workflow and its token in the repo)
            </span>
            {workers && <input value={workflow} onChange={(e) => setWorkflow(e.target.value)} aria-label="Worker workflow file" placeholder="agora-worker.yml" />}
          </label>
        )}
        <fieldset className="field">
          <span>People on it</span>
          <div className="check-grid">
            {people
              .filter((x) => x.role !== "guest" || team.includes(x.id))
              .map((x) => (
                <label key={x.id} className="check">
                  <input type="checkbox" checked={team.includes(x.id)} onChange={(e) => setTeam(e.target.checked ? [...team, x.id] : team.filter((id) => id !== x.id))} />
                  <Avatar person={x} size={28} /> {x.name}
                </label>
              ))}
          </div>
        </fieldset>
        <div className="actions">
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="submit" disabled={busy || (!project && !repo)}>
            {busy ? "Saving" : project ? "Save" : "Create project"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function NewProjectModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  return (
    <ProjectForm
      onClose={onClose}
      onSaved={async (id) => {
        onClose();
        await loadBootstrap();
        navigate(`/p/${id}`);
      }}
    />
  );
}

/** Phase 8b: what Claude may do in this project's session without asking, and whether it is delicate. */
function ClaudePolicy({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const [policy, setPolicy] = useState<{ delicate: boolean; allowed: string[]; custom: boolean; defaults: string[] } | null>(null);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    void api<NonNullable<typeof policy>>(`/api/projects/${projectId}/claude`).then(
      (p) => {
        setPolicy(p);
        setText(p.allowed.join("\n"));
      },
      () => setPolicy(null),
    );
  }, [projectId]);
  if (!policy) return null;
  const save = async (patch: { delicate?: boolean; allowed?: string[] | null }) => {
    setError(null);
    setSaved(false);
    try {
      const p = await api<NonNullable<typeof policy>>(`/api/projects/${projectId}/claude`, patch, "PATCH");
      setPolicy(p);
      setText(p.allowed.join("\n"));
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Not saved.");
    }
  };
  return (
    <Fold id="project-claude" title="Claude in this project" summary={`${policy.delicate ? "Delicate: asks before every push, merge and deploy" : "Pushes its branch without asking"} · ${policy.allowed.length} actions run without asking${policy.custom ? "" : " (defaults)"}`}>
      <p className="muted-text">
        Claude works in its own copy of the repo, on its own branch. These actions run without asking; anything else becomes a card for the owner in the room and the Work view. A merge into the default branch always asks.
      </p>
      <label className="check">
        <input type="checkbox" checked={policy.delicate} disabled={!canEdit} onChange={(e) => void save({ delicate: e.target.checked })} />
        Delicate project: always ask before a push, a pull request, a merge or anything that deploys
      </label>
      <label className="field">
        <span>Runs without asking (one rule per line)</span>
        <textarea rows={8} value={text} disabled={!canEdit} onChange={(e) => setText(e.target.value)} spellCheck={false} className="mono" />
      </label>
      <p className="muted-text">
        Rules look like <code>Bash(npm test *)</code>, <code>Edit(/**)</code> (any file in the project folder) or <code>WebFetch</code>. Choosing Always for this project on a card adds its rule here.
      </p>
      {canEdit && (
        <div className="actions">
          <button className="btn primary small" type="button" onClick={() => void save({ allowed: text.split("\n").map((l) => l.trim()).filter(Boolean) })}>
            Save the list
          </button>
          {policy.custom && (
            <button className="btn ghost small" type="button" onClick={() => void save({ allowed: null })}>
              Back to the defaults
            </button>
          )}
          {saved && <span className="muted-text">Saved. A running session picks it up when it next opens.</span>}
        </div>
      )}
      <ErrorNote error={error} />
    </Fold>
  );
}

function ProjectTasks({ projectId }: { projectId: string }) {
  const { list, error } = useTaskList(`/api/projects/${projectId}/tasks`);
  const [showDone, setShowDone] = useState(false);
  const open = (list ?? []).filter((t) => t.status !== "done" && t.status !== "cancelled").sort(byUrgency);
  const done = (list ?? []).filter((t) => t.status === "done").sort(byUrgency);
  return (
    <Fold id="project-tasks" title="Tasks" summary={list ? `${open.length} open, ${done.length} done` : undefined}>
      <ErrorNote error={error} />
      {list && !open.length && <p className="muted-text">No open tasks. Create them in the project's rooms.</p>}
      <ul className="task-list">
        {open.map((t) => (
          <TaskRow key={t.id} task={t} />
        ))}
        {showDone && done.map((t) => <TaskRow key={t.id} task={t} />)}
      </ul>
      {done.length > 0 && (
        <button className="linkish" type="button" onClick={() => setShowDone(!showDone)}>
          {showDone ? "Hide done" : `Show ${done.length} done`}
        </button>
      )}
    </Fold>
  );
}

interface OverviewData {
  checkedAt: string;
  commits: { sha: string; message: string; author: string; date: string; url: string }[] | null;
  pulls: { number: number; title: string; url: string; draft: boolean; author: string; branch: string; updatedAt: string; ci: CiState }[] | null;
  ci: CiState;
  live: { ok: boolean; status: number | null; ms: number | null; error: string | null } | null;
  deploys: { version: string; status: string; note: string; reportedAt: string }[];
  error: string | null;
}

const when = (iso: string) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
const CI_TEXT = { success: "passing", failure: "failing", pending: "running" } as const;

/** What GitHub and the live site say right now (refreshed at most once a minute, and on every webhook). */
function ProjectOverview({ projectId, repo, liveUrl }: { projectId: string; repo: string; liveUrl: string }) {
  const [data, setData] = useState<OverviewData | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setData(null);
    api<{ overview: OverviewData }>(`/api/projects/${projectId}/overview`).then(
      (r) => setData(r.overview),
      (err) => setError(err instanceof ApiError ? err.message : "Could not load the overview."),
    );
  }, [projectId]);
  const last = data?.deploys[0];
  return (
    <Fold id="project-overview" title="Overview" summary={data ? `Live site ${liveUrl ? (data.live?.ok ? "up" : "down") : "not set"} · CI ${data.ci ? CI_TEXT[data.ci] : "none"} · ${data.pulls?.length ?? 0} open pull requests` : undefined}>
      <ErrorNote error={error} />
      {!data && !error && <p className="muted-text">Reading GitHub</p>}
      {data && (
        <>
          <div className="overview-facts">
            <div>
              <small>Live site</small>
              {liveUrl ? (
                <b className={data.live?.ok ? "up" : "down"}>{data.live?.ok ? `up${data.live.ms ? `, ${data.live.ms} ms` : ""}` : `down${data.live?.status ? ` (${data.live.status})` : ""}`}</b>
              ) : (
                <b>no address set</b>
              )}
            </div>
            <div>
              <small>CI on the main branch</small>
              <b>
                {data.ci ? (
                  <>
                    <CiDot ci={data.ci} /> {CI_TEXT[data.ci]}
                  </>
                ) : (
                  "no checks"
                )}
              </b>
            </div>
            <div>
              <small>Last deploy</small>
              <b>{last ? `${last.version}, ${when(last.reportedAt)}${last.status !== "ok" ? `, ${last.status.replace("_", " ")}` : ""}` : "none reported"}</b>
            </div>
          </div>
          {data.error && <p className="muted-text small">GitHub: {data.error}</p>}
          <h3 className="sub-head">Open pull requests</h3>
          {data.pulls?.length ? (
            <ul className="rows">
              {data.pulls.map((x) => (
                <li key={x.number}>
                  <div>
                    <a href={x.url} target="_blank" rel="noreferrer">
                      #{x.number} {x.title}
                    </a>
                    <small>
                      {x.draft ? "draft · " : ""}
                      {x.author} · {x.branch} · {when(x.updatedAt)} <CiDot ci={x.ci} />
                    </small>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted-text small">None.</p>
          )}
          <h3 className="sub-head">Last commits</h3>
          {data.commits?.length ? (
            <ul className="rows">
              {data.commits.map((c) => (
                <li key={c.sha}>
                  <div>
                    <a href={c.url} target="_blank" rel="noreferrer">
                      {c.message}
                    </a>
                    <small>
                      <code>{c.sha.slice(0, 7)}</code> · {c.author} · {when(c.date)}
                    </small>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted-text small">None read.</p>
          )}
          <small className="muted-text">
            From <a href={`https://github.com/${repo}`} target="_blank" rel="noreferrer">{repo}</a>, {when(data.checkedAt)}. Deploys appear when the project's deploy script reports them.
          </small>
        </>
      )}
    </Fold>
  );
}
